// spending.js
//
// Federal spending, the companies it pays, and members' stock trades, from
// api2. Endpoints, labels and formatters for the spending page and the
// company page.
//
// ─── The rules this file builds in ────────────────────────────────────────────
//
// From the spending guide, each one stops the page saying something the
// records do not say:
//
//   1. Obligations are not payments. Every award figure is "obligated".
//   2. Say which year, and whether it is over. The fiscal year runs October
//      to September, and the current one is always partial.
//   3. The budget (/spending/functions) and the awards are two pictures of
//      different things. They are shown apart and never added.
//   4. null means none recorded, not zero. It formats as a dash.
//   5. Never add two fields the API keeps apart: in-house and outside
//      lobbying, PAC and treasury money, a contract and its subcontracts, a
//      loan's face value and a contract's obligations.
//   6. A PAC is not its company, and a member's trade is not the company's.
//   7. Scores describe activity, not results.

import { fetchApi2 } from './api2';

// ─── Fiscal year ──────────────────────────────────────────────────────────────

// October opens the next fiscal year: 1 Oct 2026 is the first day of FY2027.
export const currentFiscalYear = (now = new Date()) =>
  now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();

const daysAgoIso = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

// ─── Endpoints ────────────────────────────────────────────────────────────────

// The whole budget, or one budget function's subfunctions, agencies and
// accounts when a code is given.
export const fetchBudgetFunctions = (functionCode) =>
  fetchApi2('/spending/functions', functionCode ? { function: functionCode, top: 8 } : {});

// The current fiscal year is passed explicitly: left out, api2 defaults to
// the last complete year, which is right for rankings and wrong for "now".
export const fetchAwardOverview = (awardGroup) =>
  fetchApi2('/spending/overview', {
    fiscal_year: currentFiscalYear(),
    award_group: awardGroup,
  });

export const AWARD_FEEDS = {
  newest: { sort: 'newest', signed_within_days: 7 },
  largest: { sort: 'amount', signed_within_days: 30 },
};

// Pages by `page`, starting at 1, and says `has_next` rather than a total --
// the way USAspending itself pages.
export const AWARD_PAGE_SIZE = 10;

export const fetchAwardFeed = (feed, awardGroup, limit = AWARD_PAGE_SIZE, page = 1) =>
  fetchApi2('/spending/awards', {
    ...AWARD_FEEDS[feed],
    award_group: awardGroup === 'all' ? undefined : awardGroup,
    limit,
    page,
  });

export const fetchAward = (awardId) =>
  fetchApi2(`/spending/awards/${encodeURIComponent(awardId)}`);

export const fetchRecipientAwards = (recipientId, limit = 5) =>
  fetchApi2('/spending/awards', {
    recipient_id: recipientId,
    sort: 'newest',
    signed_within_days: 365,
    limit,
  });

export const fetchInfluenceOverview = () =>
  fetchApi2('/influence/overview', { board_size: 8, points: 0 });

// Pages by `offset`, with a `total`.
export const COMPANY_SEARCH_PAGE_SIZE = 8;

export const searchCompanies = (query, limit = COMPANY_SEARCH_PAGE_SIZE, offset = 0) =>
  fetchApi2('/influence/companies', { q: query, kind: 'all', limit, offset });

// `ticker`, not `q`: q matches part of a name, so q=BE finds Alphabet.
export const findCompanyByTicker = (ticker) =>
  fetchApi2('/influence/companies', { ticker, kind: 'all', limit: 1 }).then(
    (body) => body?.results?.[0] || null
  );

export const fetchCompany = (orgId) =>
  fetchApi2(`/influence/companies/${encodeURIComponent(orgId)}`);

export const TRADE_WINDOWS = [30, 90, 365];

// Pages by `offset`, with a `total`.
export const TRADE_PAGE_SIZE = 12;

export const fetchLargestTrades = (days, limit = TRADE_PAGE_SIZE, offset = 0) =>
  fetchApi2('/disclosures/trades', { sort: 'largest', since: daysAgoIso(days), limit, offset });

export const fetchTradesForTicker = (ticker, limit = 8) =>
  fetchApi2('/disclosures/trades', { ticker, sort: 'largest', limit });

// ─── Labels ───────────────────────────────────────────────────────────────────

export const AWARD_GROUPS = ['all', 'contracts', 'grants', 'direct_payments', 'loans'];

export const awardGroupLabel = (group, t) =>
  ({
    all: t('common:allAwards', 'All'),
    contracts: t('common:contracts', 'Contracts'),
    grants: t('common:grants', 'Grants'),
    direct_payments: t('common:directPayments', 'Direct payments'),
    loans: t('common:loans', 'Loans'),
  })[group] || group;

// Loans in award totals are subsidy cost -- what the government expects to
// lose on them -- and net of re-estimates that can be negative. The label says
// so where the number is.
export const awardTypeLabel = (type, t) =>
  ({
    contract: t('common:awardTypeContract', 'Contracts'),
    grant: t('common:awardTypeGrant', 'Grants'),
    direct: t('common:awardTypeDirect', 'Direct payments'),
    idv: t('common:awardTypeIdv', 'Contract vehicles (IDVs)'),
    loan: t('common:awardTypeLoan', 'Loans (subsidy cost; can be negative)'),
    other: t('common:awardTypeOther', 'Other assistance'),
  })[type] || type;

// MULTIPLE RECIPIENTS is USAspending's label for money not attributed to one
// payee -- benefits paid to millions of people, aggregated small awards. It
// is not an organisation, and has no recipient_id.
export const recipientLabel = (row, t) =>
  row?.recipient_id || (row?.name && row.name !== 'MULTIPLE RECIPIENTS')
    ? row.name
    : t('common:notAttributed', 'Not attributed to a named recipient');

export const tradeOwnerLabel = (owner, t) =>
  ({
    self: t('common:ownerSelf', 'Member'),
    spouse: t('common:ownerSpouse', 'Spouse'),
    joint: t('common:ownerJoint', 'Joint'),
    'dependent child': t('common:ownerChild', 'Dependent child'),
  })[owner] || owner;

export const txTypeLabel = (type, t) =>
  ({
    purchase: t('common:txPurchase', 'Purchase'),
    sale: t('common:txSale', 'Sale'),
    'partial sale': t('common:txPartialSale', 'Partial sale'),
    exchange: t('common:txExchange', 'Exchange'),
  })[type] || type;

// A member's record page, by chamber. Former members have no bioguide id in
// the trades feed, and get no link.
export const memberPath = (bioguideId, chamber) => {
  if (!bioguideId) return null;
  return chamber === 'senate'
    ? `/us/senator/${bioguideId}`
    : `/us/representative/${bioguideId}`;
};

export const companyPath = (orgId) => `/us/company/${encodeURIComponent(orgId)}`;

export const companyRecordUrl = (orgId) =>
  `https://api2.govglance.org/app/#/influence-company/${encodeURIComponent(orgId)}`;

// ─── Formatting ───────────────────────────────────────────────────────────────

// The guide's formatter: a trillions tier, because the budget runs to $9.4tn;
// a sign, because net loan obligations can be negative; and a dash for
// anything that is not a number, never "$0". Each threshold sits where
// rounding would carry into the next unit, so 999,700 is "$1.0m", not
// "$1000k".
export const formatDollars = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '\u2014';
  const a = Math.abs(n);
  const s = n < 0 ? '-' : '';
  if (a >= 9.99995e11) return `${s}$${(a / 1e12).toFixed(2)}tn`;
  if (a >= 9.99995e8) return `${s}$${(a / 1e9).toFixed(2)}bn`;
  if (a >= 9.995e5) return `${s}$${(a / 1e6).toFixed(1)}m`;
  if (a >= 999.5) return `${s}$${Math.round(a / 1e3)}k`;
  return `${s}$${Math.round(a)}`;
};

export const formatPercent = (x) =>
  typeof x !== 'number' || Number.isNaN(x) ? '\u2014' : `${(x * 100).toFixed(x >= 0.1 ? 0 : 1)}%`;

export const formatScore = (x) =>
  typeof x !== 'number' || Number.isNaN(x) ? '\u2014' : x.toFixed(1);

export const formatCount = (x) =>
  typeof x !== 'number' || Number.isNaN(x) ? '\u2014' : x.toLocaleString();

// Calendar days from the API, not instants: parsed as local dates so an
// evening reader west of London does not see the day before.
export const formatDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const SMALL_WORDS = new Set(['of', 'and', 'the', 'for', 'in', 'on', 'a', 'an', 'to', 'at', 'by']);
const KEEP_UPPER = new Set([
  'LLC', 'INC', 'LP', 'LLP', 'PLC', 'NA', 'USA', 'US', 'DC', 'PAC', 'IBM', 'AT&T', 'II', 'III',
  'IV', 'MIT', 'UCLA', 'NASA', 'VA', 'DOD', 'HHS', 'IDV', 'IT', 'AI',
]);

// Recipient and company names are filed in capitals. Title case reads as a
// name; known acronyms stay as filed. Mixed-case input is left alone.
export const titleCaseName = (value) => {
  if (!value || typeof value !== 'string') return '';
  if (value !== value.toUpperCase()) return value;
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      const upper = word.toUpperCase();
      if (KEEP_UPPER.has(upper.replace(/[.,]/g, ''))) return upper;
      if (index > 0 && SMALL_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Drill-down responses hold several ranked lists (subfunctions, agencies,
// accounts) under keys that differ by level. Every list of named amounts is
// found rather than named, so a new list in the response shows up without a
// code change.
export const rankedLists = (body) =>
  Object.entries(body || {})
    .filter(
      ([, value]) =>
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((row) => row && typeof row === 'object' && 'name' in row && 'amount' in row)
    )
    .map(([key, rows]) => ({ key, rows }));

export const listTitle = (key, t) =>
  ({
    functions: t('common:budgetFunctions', 'By purpose'),
    subfunctions: t('common:subfunctions', 'Within it'),
    by_object_class: t('common:byObjectClass', 'By kind of spending'),
    by_agency: t('common:byAgency', 'By agency'),
    agencies: t('common:byAgency', 'By agency'),
    accounts: t('common:federalAccounts', 'Largest federal accounts'),
    federal_accounts: t('common:federalAccounts', 'Largest federal accounts'),
    top_funding_agencies: t('common:fundingAgencies', 'Agencies awarding it'),
    top_recipients: t('common:recipients', 'Recipients'),
    top_purchases: t('common:purchases', 'What was bought'),
  })[key] ||
  key
    .replace(/^by_/, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());